import { authNext } from "./authNavigation";

export type VerificationUser = {
  id?: number | string;
  is_admin?: boolean;
  is_merchant?: boolean;
  verification_exempt?: boolean;
  ekyc_status?: string | null;
  account_status?: string | null;
  is_social?: boolean;
  social_provider?: string | null;
  social_id?: string | null;
};

export function isVerificationExempt(user: VerificationUser | null | undefined): boolean {
  return user?.is_admin === true || user?.verification_exempt === true;
}

export function isVerificationComplete(user: VerificationUser | null | undefined, requireBoth = false): boolean {
  if (!user) return false;
  if (isVerificationExempt(user)) return true;
  const identityDone = user.ekyc_status === "verified";
  const accountDone = user.account_status === "verified";
  const social = user.is_social === true || !!user.social_provider || !!user.social_id;
  return social && !requireBoth ? identityDone || accountDone : identityDone && accountDone;
}

/** A new request already required ID verification; check it before documents. */
export function isRequestVerificationComplete(user: VerificationUser | null | undefined): boolean {
  return isVerificationComplete(user) && (isVerificationExempt(user) || user?.ekyc_status === "verified");
}

export function isNewContractRequest(value: unknown): boolean {
  const next = authNext(value);
  return next !== null && /^\/contracts\/new(?:\/|$)/.test(next.split(/[?#]/, 1)[0]);
}

/** Signup and unrelated payment routes retain their existing verification policy. */
export function isVerificationCompleteForRoute(user: VerificationUser | null | undefined, next: string): boolean {
  return isNewContractRequest(next) ? isRequestVerificationComplete(user) : isVerificationComplete(user);
}

/** Financial routes require a fresh status read, not a different signup policy. */
export function requiresStrictFinancialVerification(value: string): boolean {
  const pathname = (authNext(value) || "/").split(/[?#]/, 1)[0];
  return /^\/(pay|pay-now)\/?$/.test(pathname) || /^\/pay\/[^/]+\/edit\/?$/.test(pathname) || /^\/contracts(?:\/|$)/.test(pathname);
}

/** Unwrap legacy verification returns without ever landing in a verification loop. */
export function verificationNext(value: unknown, fallback = "/start"): string {
  const isVerificationPath = (path: string) => /^\/(onboarding|account\/verify|ekyc\/reverify)(?:\/|$)/.test(path.split(/[?#]/, 1)[0]);
  const safeFallback = authNext(fallback);
  const defaultPath = safeFallback && !isVerificationPath(safeFallback) ? safeFallback : "/start";
  let candidate = authNext(value);
  const visited = new Set<string>();
  for (let depth = 0; candidate && depth < 5; depth += 1) {
    if (!isVerificationPath(candidate)) return candidate;
    if (visited.has(candidate)) break;
    visited.add(candidate);
    candidate = authNext(new URL(candidate, window.location.origin).searchParams.get("next"));
  }
  return defaultPath;
}
